import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Check, X, Clock, Users, Filter, Send } from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/context/ToastContext'

interface BetaRequest {
  id: string
  email: string
  name: string
  message: string | null
  status: 'pending' | 'approved' | 'rejected'
  created_at: string
  reviewed_at: string | null
  notes: string | null
}

type StatusFilter = 'pending' | 'approved' | 'rejected' | 'all'

/**
 * BetaRequests Admin Page
 *
 * Displays beta access requests and allows admin to approve/reject them.
 * Uses direct Supabase queries with RLS (admin-only policies).
 */
export function BetaRequests() {
  const { showToast } = useToast()
  const [requests, setRequests] = useState<BetaRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending')
  const [processingId, setProcessingId] = useState<string | null>(null)

  const fetchRequests = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('beta_requests')
        .select('*')
        .order('created_at', { ascending: false })

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw new Error(fetchError.message)
      }

      setRequests(data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch requests')
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  const handleApprove = async (request: BetaRequest) => {
    const adminNotes = prompt(`Enter notes for approving ${request.email} (optional):`)
    if (adminNotes === null) return // User cancelled

    setProcessingId(request.id)

    try {
      const { data, error: rpcError } = await supabase.rpc('approve_beta_request', {
        p_email: request.email,
        p_notes: adminNotes || null,
      })

      if (rpcError) {
        throw new Error(rpcError.message)
      }

      const result = data as { ok: boolean; error?: string; name?: string }

      if (!result.ok) {
        throw new Error(result.error || 'Failed to approve')
      }

      // Send Supabase invite with magic link
      const session = await supabase.auth.getSession()
      const accessToken = session.data.session?.access_token

      if (accessToken) {
        const inviteRes = await fetch('/api/invite-user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            email: request.email,
            name: request.name,
          }),
        })

        const inviteData = await inviteRes.json()

        if (inviteData.ok) {
          showToast(`Approved and invited ${request.name}`, 'success')
        } else {
          showToast(`Approved ${request.name}, but invite failed: ${inviteData.error}`, 'info')
        }
      } else {
        showToast(`Approved ${request.name} (no invite sent - session issue)`, 'info')
      }

      // Refresh the list
      await fetchRequests()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to approve', 'error')
    } finally {
      setProcessingId(null)
    }
  }

  const handleReject = async (request: BetaRequest) => {
    const adminNotes = prompt(`Enter notes for rejecting ${request.email} (optional):`)
    if (adminNotes === null) return // User cancelled

    setProcessingId(request.id)

    try {
      const { data, error: rpcError } = await supabase.rpc('reject_beta_request', {
        p_email: request.email,
        p_notes: adminNotes || null,
      })

      if (rpcError) {
        throw new Error(rpcError.message)
      }

      const result = data as { ok: boolean; error?: string }

      if (!result.ok) {
        throw new Error(result.error || 'Failed to reject')
      }

      showToast(`Rejected ${request.name}`, 'success')

      // Refresh the list
      await fetchRequests()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to reject', 'error')
    } finally {
      setProcessingId(null)
    }
  }

  const handleResendInvite = async (request: BetaRequest) => {
    setProcessingId(request.id)

    try {
      const session = await supabase.auth.getSession()
      const accessToken = session.data.session?.access_token

      if (!accessToken) {
        showToast('Session expired. Please refresh the page.', 'error')
        return
      }

      const inviteRes = await fetch('/api/invite-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email: request.email,
          name: request.name,
        }),
      })

      const inviteData = await inviteRes.json()

      if (inviteData.ok) {
        showToast(`Invite resent to ${request.name}`, 'success')
      } else {
        showToast(`Failed to resend: ${inviteData.error}`, 'error')
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to resend invite', 'error')
    } finally {
      setProcessingId(null)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const getStatusCounts = () => {
    if (statusFilter !== 'all') {
      return { pending: 0, approved: 0, rejected: 0 }
    }
    return {
      pending: requests.filter((r) => r.status === 'pending').length,
      approved: requests.filter((r) => r.status === 'approved').length,
      rejected: requests.filter((r) => r.status === 'rejected').length,
    }
  }

  const statusCounts = getStatusCounts()

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[var(--color-kids-surface)] border-b border-[var(--color-kids-border)] px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)]">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Users className="w-6 h-6 text-[var(--color-kids-primary)]" />
              <h1 className="font-display text-xl font-bold text-[var(--color-kids-text)]">
                Beta Requests
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Filter Tabs */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-[var(--color-kids-text-muted)]" />
          <span className="text-sm text-[var(--color-kids-text-muted)]">Filter:</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(['pending', 'approved', 'rejected', 'all'] as StatusFilter[]).map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                statusFilter === status
                  ? 'bg-[var(--color-kids-primary)] text-white'
                  : 'bg-[var(--color-kids-surface)] text-[var(--color-kids-text-muted)] border border-[var(--color-kids-border)] hover:border-[var(--color-kids-primary)]'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
              {status !== 'all' && statusFilter === 'all' && ` (${statusCounts[status]})`}
            </button>
          ))}
        </div>
      </div>

      {/* Request List */}
      <div className="max-w-6xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-[var(--color-kids-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[var(--color-kids-text-muted)]">Loading requests...</p>
          </div>
        ) : error ? (
          <Card>
            <CardContent className="p-6">
              <div className="text-red-600">{error}</div>
            </CardContent>
          </Card>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 text-[var(--color-kids-text-muted)]">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No {statusFilter !== 'all' ? statusFilter : ''} requests found.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <Card key={request.id}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-lg font-semibold text-[var(--color-kids-text)]">
                          {request.name}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            request.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : request.status === 'approved'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {request.status}
                        </span>
                      </div>
                      <p className="text-[var(--color-kids-text-muted)] mt-1">{request.email}</p>

                      {request.message && (
                        <div className="mt-3 p-3 bg-[var(--color-kids-bg)] rounded-lg">
                          <p className="text-[var(--color-kids-text)] text-sm italic">
                            "{request.message}"
                          </p>
                        </div>
                      )}

                      <div className="mt-3 flex items-center gap-4 text-xs text-[var(--color-kids-text-muted)]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Submitted: {formatDate(request.created_at)}
                        </span>
                        {request.reviewed_at && (
                          <span>Reviewed: {formatDate(request.reviewed_at)}</span>
                        )}
                      </div>

                      {request.notes && (
                        <p className="mt-2 text-xs text-[var(--color-kids-text-muted)]">
                          Notes: {request.notes}
                        </p>
                      )}
                    </div>

                    <div className="flex gap-2 shrink-0">
                      {request.status === 'pending' && (
                        <>
                          <Button
                            onClick={() => handleApprove(request)}
                            disabled={processingId === request.id}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            {processingId === request.id ? '...' : 'Approve'}
                          </Button>
                          <Button
                            onClick={() => handleReject(request)}
                            disabled={processingId === request.id}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <X className="w-4 h-4 mr-1" />
                            {processingId === request.id ? '...' : 'Reject'}
                          </Button>
                        </>
                      )}
                      {request.status === 'approved' && (
                        <Button
                          onClick={() => handleResendInvite(request)}
                          disabled={processingId === request.id}
                          variant="secondary"
                          size="sm"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          {processingId === request.id ? '...' : 'Resend Invite'}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
